import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
} from "react";
import { ApiError } from "../services/api";
import homeContentService, {
  Artist,
  NewsArticle,
  Track,
  CollaborationRequest,
} from "../services/homeContent";

// Define error types
type HomeContentError = {
  message: string;
  code?: string;
  details?: unknown;
};

// Define the context state type
interface HomeContentState {
  trendingArtists: Artist[];
  featuredArtists: Artist[];
  latestNews: NewsArticle[];
  popularTracks: Track[];
  recentCollaborations: CollaborationRequest[];
  isLoading: boolean;
  error: HomeContentError | null;
}

// Define the context type
interface HomeContentContextType extends HomeContentState {
  refreshContent: () => Promise<void>;
  refreshTrendingArtists: () => Promise<void>;
  refreshFeaturedArtists: () => Promise<void>;
  refreshLatestNews: () => Promise<void>;
  refreshPopularTracks: () => Promise<void>;
  refreshRecentCollaborations: () => Promise<void>;
}

// Create the context
const HomeContentContext = createContext<HomeContentContextType | null>(null);

// Provider props
interface HomeContentProviderProps {
  children: ReactNode;
}

// Provider component
export const HomeContentProvider: React.FC<HomeContentProviderProps> = ({
  children,
}) => {
  const [state, setState] = useState<HomeContentState>({
    trendingArtists: [],
    featuredArtists: [],
    latestNews: [],
    popularTracks: [],
    recentCollaborations: [],
    isLoading: true,
    error: null,
  });

  // Refresh functions
  const refreshTrendingArtists = useCallback(async () => {
    try {
      const artists = await homeContentService.getTrendingArtists();
      setState((prev) => ({
        ...prev,
        trendingArtists: artists,
        error: null,
      }));
    } catch (error) {
      console.error("Error fetching trending artists:", error);
      const contentError: HomeContentError = {
        message:
          error instanceof Error
            ? error.message
            : "Failed to fetch trending artists",
        code: error instanceof ApiError ? String(error.status) : undefined,
        details: error,
      };
      setState((prev) => ({
        ...prev,
        error: contentError,
      }));
    }
  }, []);

  const refreshFeaturedArtists = useCallback(async () => {
    try {
      const artists = await homeContentService.getFeaturedArtists();
      setState((prev) => ({
        ...prev,
        featuredArtists: artists,
        error: null,
      }));
    } catch (error) {
      console.error("Error fetching featured artists:", error);
      const contentError: HomeContentError = {
        message:
          error instanceof Error
            ? error.message
            : "Failed to fetch featured artists",
        code: error instanceof ApiError ? String(error.status) : undefined,
        details: error,
      };
      setState((prev) => ({
        ...prev,
        error: contentError,
      }));
    }
  }, []);

  const refreshLatestNews = useCallback(async () => {
    try {
      const news = await homeContentService.getLatestNews();
      setState((prev) => ({
        ...prev,
        latestNews: news,
        error: null,
      }));
    } catch (error) {
      console.error("Error fetching latest news:", error);
      const contentError: HomeContentError = {
        message:
          error instanceof Error
            ? error.message
            : "Failed to fetch latest news",
        code: error instanceof ApiError ? String(error.status) : undefined,
        details: error,
      };
      setState((prev) => ({
        ...prev,
        error: contentError,
      }));
    }
  }, []);

  const refreshPopularTracks = useCallback(async () => {
    try {
      const tracks = await homeContentService.getPopularTracks();
      setState((prev) => ({
        ...prev,
        popularTracks: tracks,
        error: null,
      }));
    } catch (error) {
      console.error("Error fetching popular tracks:", error);
      const contentError: HomeContentError = {
        message:
          error instanceof Error
            ? error.message
            : "Failed to fetch popular tracks",
        code: error instanceof ApiError ? String(error.status) : undefined,
        details: error,
      };
      setState((prev) => ({
        ...prev,
        error: contentError,
      }));
    }
  }, []);

  const refreshRecentCollaborations = useCallback(async () => {
    try {
      const collaborations = await homeContentService.getRecentCollaborations();
      setState((prev) => ({
        ...prev,
        recentCollaborations: collaborations,
        error: null,
      }));
    } catch (error) {
      console.error("Error fetching recent collaborations:", error);
      const contentError: HomeContentError = {
        message:
          error instanceof Error
            ? error.message
            : "Failed to fetch recent collaborations",
        code: error instanceof ApiError ? String(error.status) : undefined,
        details: error,
      };
      setState((prev) => ({
        ...prev,
        error: contentError,
      }));
    }
  }, []);

  const refreshContent = useCallback(async () => {
    setState((prev) => ({ ...prev, isLoading: true, error: null }));

    try {
      const content = await homeContentService.getHomeContent();
      setState((prev) => ({
        ...prev,
        ...content,
        isLoading: false,
        error: null,
      }));
    } catch (error) {
      console.error("Error fetching home content:", error);
      const contentError: HomeContentError = {
        message:
          error instanceof Error ? error.message : "Failed to fetch content",
        code: error instanceof ApiError ? String(error.status) : undefined,
        details: error,
      };
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: contentError,
      }));
    }
  }, []);

  // Initial load - Removed to prevent redundant fetching on every page navigation
  // useEffect(() => {
  //   refreshContent();
  // }, [refreshContent]);

  const value: HomeContentContextType = {
    ...state,
    refreshContent,
    refreshTrendingArtists,
    refreshFeaturedArtists,
    refreshLatestNews,
    refreshPopularTracks,
    refreshRecentCollaborations,
  };

  return (
    <HomeContentContext.Provider value={value}>
      {children}
    </HomeContentContext.Provider>
  );
};

// Custom hook for using home content context
export const useHomeContent = () => {
  const context = useContext(HomeContentContext);
  if (!context) {
    throw new Error("useHomeContent must be used within a HomeContentProvider");
  }
  return context;
};

export default HomeContentContext;
